import { useState, useEffect, useCallback } from 'react';
import offlineDb from '../services/offlineDb';
import syncManager from '../services/syncManager';

/**
 * Hook per gestire le letture offline
 * Fornisce metodi per salvare, leggere e sincronizzare le letture
 */
export default function useOfflineSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Aggiorna conteggio pendenti
  const refreshPendingCount = useCallback(async () => {
    const pending = await offlineDb.getPendingReadings();
    setPendingCount(pending.length);
  }, []);

  // Ascolta eventi sync manager
  useEffect(() => {
    const unsubscribe = syncManager.subscribe((event) => {
      switch (event.type) {
        case 'online': setIsOnline(true); break;
        case 'offline': setIsOnline(false); break;
        case 'uploading': case 'downloading': setIsSyncing(true); setSyncError(null); break;
        case 'sync-complete': case 'upload-complete': case 'downloaded':
          setIsSyncing(false);
          refreshPendingCount();
          offlineDb.getLastSyncTime().then(setLastSync);
          break;
        case 'sync-error': case 'upload-error': case 'download-error':
          setIsSyncing(false);
          setSyncError(event.error);
          break;
        default: break;
      }
    });

    // Stato iniziale
    refreshPendingCount();
    offlineDb.getLastSyncTime().then(setLastSync);

    // Online/offline listeners
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refreshPendingCount]);

  // Salva batch localmente (offline)
  const saveLocally = useCallback(async (batch) => {
    const saved = await offlineDb.savePendingReading(batch);
    await refreshPendingCount();
    return saved;
  }, [refreshPendingCount]);

  // Forza sincronizzazione
  const syncNow = useCallback(async () => {
    setSyncError(null);
    await syncManager.syncAll();
  }, []);

  // Download dati per offline
  const downloadData = useCallback(async () => {
    await syncManager.downloadData();
  }, []);

  return {
    pendingCount,
    isOnline,
    isSyncing,
    lastSync,
    syncError,
    saveLocally,
    syncNow,
    downloadData,
    refreshPendingCount
  };
}
